import React from 'react';
import PropTypes from 'prop-types';
import {
  Grid, Header, Button, Modal, Icon, Message,
} from 'semantic-ui-react';
import moment from 'moment';
import numeral from 'numeral';
import { utcMoment } from '../../utilities/dateUtils';
import '../../assets/vraPendingStyle.css';

export const GRACE_PERIOD_DAYS = 3;

const formatCurrency = value => (value === null || value === undefined ? '' : `$ ${numeral(value).format('0,0')}`);
const formatDateTime = value => (value ? utcMoment(value).format('MM/DD/YYYY') : '');

const ConfirmGuaranteeExtension = props => {
  const {
    pendingVRA, extensionDays, open, onBackHandler, onProceedHandler, submitting,
  } = props;

  const { guaranteePurchase } = pendingVRA;
  const guaranteeOffer = guaranteePurchase?.guaranteeOffer;
  const vehicleInformation = guaranteeOffer?.vehicleInformation;
  const originalExpiration = guaranteePurchase?.expiresAt;
  const originalExpirationDate = utcMoment(originalExpiration);
  const newExpirationDate = moment.utc(originalExpiration).add(extensionDays, 'days').local();

  return (
    <Modal
      open={open}
      closeOnEscape={false}
      onClose={onBackHandler}
    >
      <Modal.Header>Confirm Guarantee Extension</Modal.Header>
      <Modal.Content image scrolling>
        <Modal.Description>
          <Message warning icon>
            <Icon name='exclamation triangle' />
            <Message.Content>
              <Message.Header>Guarantee Has Expired</Message.Header>
              {`This guarantee expired during the pending review period. A ${GRACE_PERIOD_DAYS}-day grace `
                + 'period extension will be applied automatically upon approval.'}
            </Message.Content>
          </Message>

          <Header size='tiny' className='extensionSectionHeader'>Guarantee Extension Details</Header>
          <Grid className='extensionInfoBox'>
            <Grid.Row className='detailRow'>
              <Grid.Column width={4}>
                <Header size='tiny'>Original Expiration:</Header>
              </Grid.Column>
              <Grid.Column width={12} textAlign='right'>
                <span className='originalExpiration'>{originalExpiration ? formatDateTime(originalExpirationDate) : ''}</span>
              </Grid.Column>
            </Grid.Row>

            <Grid.Row className='detailRow'>
              <Grid.Column width={4}>
                <Header size='tiny'>New Expiration:</Header>
              </Grid.Column>
              <Grid.Column width={12} textAlign='right'>
                <span className='newExpiration'>{originalExpiration ? formatDateTime(newExpirationDate) : ''}</span>
              </Grid.Column>
            </Grid.Row>

            <Grid.Row>
              <Grid.Column width={4}>
                <Header size='tiny'>Grace Period:</Header>
              </Grid.Column>
              <Grid.Column width={12} textAlign='right'>{GRACE_PERIOD_DAYS} Days</Grid.Column>
            </Grid.Row>
          </Grid>

          <Header size='tiny' className='extensionSectionHeader'>Offer Summary</Header>
          <Grid className='extensionInfoBox'>
            <Grid.Row className='detailRow'>
              <Grid.Column width={4}>
                <Header size='tiny'>Offer Number:</Header>
              </Grid.Column>
              <Grid.Column width={12} textAlign='right'>{guaranteeOffer?.id}</Grid.Column>
            </Grid.Row>

            <Grid.Row className='detailRow'>
              <Grid.Column width={4}>
                <Header size='tiny'>Vehicle:</Header>
              </Grid.Column>
              <Grid.Column width={12} textAlign='right'>
                {vehicleInformation?.year} {vehicleInformation?.make} {vehicleInformation?.model}
              </Grid.Column>
            </Grid.Row>

            <Grid.Row>
              <Grid.Column width={4}>
                <Header size='tiny'>Price:</Header>
              </Grid.Column>
              <Grid.Column width={12} textAlign='right'>{formatCurrency(guaranteeOffer?.guaranteePrice)}</Grid.Column>
            </Grid.Row>
          </Grid>
        </Modal.Description>
      </Modal.Content>
      <Modal.Actions>
        <Button onClick={onBackHandler} disabled={submitting}>
          <Icon name='arrow left' /> Back
        </Button>
        <Button
          color='green'
          onClick={onProceedHandler}
          loading={submitting}
          disabled={submitting}
          inverted
        >
          <Icon name='checkmark' /> Proceed with Extension
        </Button>
      </Modal.Actions>
    </Modal>
  );
};

ConfirmGuaranteeExtension.propTypes = {
  pendingVRA: PropTypes.shape({
    expirationDate: PropTypes.string,
    guaranteePurchase: PropTypes.shape({
      expiresAt: PropTypes.string,
      guaranteeOffer: PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        guaranteePrice: PropTypes.number,
        vehicleInformation: PropTypes.shape({
          year: PropTypes.number,
          make: PropTypes.string,
          model: PropTypes.string,
        }),
      }),
    }),
  }).isRequired,
  extensionDays: PropTypes.number.isRequired,
  open: PropTypes.bool.isRequired,
  onBackHandler: PropTypes.func.isRequired,
  onProceedHandler: PropTypes.func.isRequired,
  submitting: PropTypes.bool,
};

export default ConfirmGuaranteeExtension;
